import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { History, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { assistantsApi, type Assistant, type AssistantVersion } from "../api";

const ACTION_LABELS: Record<AssistantVersion["action"], string> = {
  create: "Created",
  save: "Saved",
  publish: "Publish toggled",
  restore: "Restored",
};

type Props = {
  assistant: Assistant;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/** Vapi-style version history: every save/publish is a snapshot; any older
 * version can be restored (which itself becomes a new version). */
export default function VersionHistory({ assistant, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const versionsQ = useQuery({
    queryKey: ["assistant-versions", assistant.id],
    queryFn: () => assistantsApi.versions(assistant.id),
    enabled: open,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const restoreMut = useMutation({
    mutationFn: (versionId: string) =>
      assistantsApi.restoreVersion(assistant.id, versionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assistants"] });
      qc.invalidateQueries({ queryKey: ["assistant-versions", assistant.id] });
      toast.success("Version restored.");
    },
    onError: () => toast.error("Couldn't restore this version. Please try again."),
  });

  const versions = versionsQ.data ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="size-4" />
            Version History
          </DialogTitle>
          <DialogDescription>
            Every save is a snapshot. Restore any version — the current state
            is kept as a version too, so nothing is lost.
          </DialogDescription>
        </DialogHeader>

        {versionsQ.isLoading ? (
          <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Loading versions…
          </div>
        ) : versions.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">
            No versions yet — save the assistant once to start its history.
          </p>
        ) : (
          <div className="max-h-96 overflow-y-auto rounded-lg border">
            {versions.map((v, i) => (
              <div
                key={v.id}
                className="flex items-center gap-3 border-b px-3 py-2.5 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-muted-foreground">
                      {v.short_id}
                    </span>
                    {i === 0 && (
                      <span className="rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-medium text-emerald-500">
                        Current
                      </span>
                    )}
                    <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                      {ACTION_LABELS[v.action] ?? v.action}
                    </span>
                  </div>
                  <div className="mt-0.5 truncate text-xs text-muted-foreground">
                    {new Date(v.created_at).toLocaleString()}
                    {v.created_by && ` · ${v.created_by}`}
                  </div>
                </div>
                {i > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={restoreMut.isPending}
                    onClick={() => restoreMut.mutate(v.id)}
                  >
                    {restoreMut.isPending && restoreMut.variables === v.id ? (
                      <Loader2 className="size-3.5 animate-spin" />
                    ) : (
                      <RotateCcw className="size-3.5" />
                    )}
                    Restore
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
